/**
 * Turni: riusa services/hrNormativeStandards.js parseTurno, che ripacchetta
 * orario/durata/weekend sui campi normativa/descrizione/mappingmansioni
 * dell'anagrafica generica (stesso modello dati del vecchio PHC_HR — non
 * cambiato qui, vedi piano). Il form nasconde questo dettaglio dietro campi
 * dedicati.
 */
import React, { useState } from "react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import {
  Card,
  Spinner,
  ErrorBox,
  SimpleTable,
} from "../../components/ui/basics";
import { TIPO } from "../../constants/normativeTipo";

function turnoToForm(t) {
  if (!t)
    return {
      oraInizio: "06:00",
      oraFine: "14:00",
      durataOre: "8",
      weekend: true,
    };
  const [oraInizio, oraFine] = (t.normativa || "00:00|00:00").split("|");
  let weekend = true;
  try {
    weekend = t.mappingmansioni
      ? JSON.parse(t.mappingmansioni).sabato !== false
      : true;
  } catch {
    /* default */
  }
  return {
    codice: t.codice,
    name: t.name,
    ordine: t.ordine,
    oraInizio,
    oraFine,
    durataOre: t.descrizione || "8",
    notturno: !!t.etrasversale,
    weekend,
  };
}

function formToPayload(values) {
  return {
    codice: values.codice,
    name: values.name,
    ordine: values.ordine ? Number(values.ordine) : null,
    tipo: TIPO.TURNO,
    normativa: `${values.oraInizio}|${values.oraFine}`,
    descrizione: String(values.durataOre),
    etrasversale: !!values.notturno,
    mappingmansioni: JSON.stringify({
      sabato: !!values.weekend,
      domenica: !!values.weekend,
    }),
  };
}

function TurnoModal({ record, onSubmit, onClose }) {
  const [values, setValues] = useState(turnoToForm(record));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit(formToPayload(values));
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">
            {record ? `Modifica ${record.name}` : "Nuovo turno"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <label className="block text-sm">
            <span className="block text-xs text-gray-500 mb-1">Codice *</span>
            <input
              required
              disabled={!!record}
              value={values.codice || ""}
              onChange={(e) => set("codice", e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 w-full disabled:bg-gray-100"
            />
          </label>
          <label className="block text-sm">
            <span className="block text-xs text-gray-500 mb-1">Nome *</span>
            <input
              required
              value={values.name || ""}
              onChange={(e) => set("name", e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 w-full"
            />
          </label>
          <div className="flex gap-2">
            <label className="block text-sm flex-1">
              <span className="block text-xs text-gray-500 mb-1">Dalle</span>
              <input
                type="time"
                value={values.oraInizio}
                onChange={(e) => set("oraInizio", e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 w-full"
              />
            </label>
            <label className="block text-sm flex-1">
              <span className="block text-xs text-gray-500 mb-1">Alle</span>
              <input
                type="time"
                value={values.oraFine}
                onChange={(e) => set("oraFine", e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 w-full"
              />
            </label>
            <label className="block text-sm w-28">
              <span className="block text-xs text-gray-500 mb-1">
                Durata (h)
              </span>
              <input
                type="text"
                inputMode="decimal"
                value={values.durataOre}
                onChange={(e) => set("durataOre", e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 w-full"
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!values.notturno}
              onChange={(e) => set("notturno", e.target.checked)}
            />
            Notturno
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!values.weekend}
              onChange={(e) => set("weekend", e.target.checked)}
            />
            Applicabile sabato/domenica
          </label>
          <label className="block text-sm">
            <span className="block text-xs text-gray-500 mb-1">Ordine</span>
            <input
              type="text"
              inputMode="numeric"
              value={values.ordine ?? ""}
              onChange={(e) => set("ordine", e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 w-full"
            />
          </label>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-primary-600 text-white text-sm px-4 py-1.5 rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TurniSection() {
  const { apiRequest } = useAuth();
  const { data, loading, error, reload } = useApi("/normative/turni");
  const [modal, setModal] = useState(null); // { record? } — assente = crea

  const submit = async (payload) => {
    if (modal.record) {
      await apiRequest(`/normative/anagrafiche/${modal.record.id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
    } else {
      await apiRequest("/normative/anagrafiche", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    }
    reload();
  };

  const elimina = async (r) => {
    if (!window.confirm(`Eliminare il turno "${r.name}"?`)) return;
    await apiRequest(`/normative/anagrafiche/${r.id}`, { method: "DELETE" });
    reload();
  };

  return (
    <Card
      title="Turni"
      actions={
        <button
          onClick={() => setModal({})}
          className="text-sm bg-primary-600 text-white px-3 py-1.5 rounded hover:bg-primary-700"
        >
          + Nuovo turno
        </button>
      }
    >
      {loading && <Spinner />}
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data && (
        <SimpleTable
          rowKey={(r) => r.id}
          columns={[
            { key: "codice", label: "Codice" },
            { key: "name", label: "Nome" },
            {
              key: "orario",
              label: "Fascia",
              render: (r) =>
                turnoToForm(r).oraInizio + " – " + turnoToForm(r).oraFine,
            },
            {
              key: "durata",
              label: "Durata (h)",
              align: "right",
              render: (r) => r.descrizione,
            },
            {
              key: "notturno",
              label: "Notturno",
              render: (r) => (r.etrasversale ? "Sì" : "No"),
            },
            {
              key: "azioni",
              label: "",
              render: (r) => (
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setModal({ record: r })}
                    className="text-xs text-primary-600 hover:underline"
                  >
                    Modifica
                  </button>
                  <button
                    onClick={() => elimina(r)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Elimina
                  </button>
                </div>
              ),
            },
          ]}
          rows={data}
        />
      )}
      {modal && (
        <TurnoModal
          record={modal.record}
          onSubmit={submit}
          onClose={() => setModal(null)}
        />
      )}
    </Card>
  );
}
